// Reusable multi-contact card list. Renders a list of saved cards with an
// inline add/edit form. Backing REST endpoints follow the pattern
//   GET /dogs/{id}/{path}  → { items: [...] }
//   POST /dogs/{id}/{path} → new item
//   PUT /dogs/{id}/{path}/{item_id}
//   DELETE /dogs/{id}/{path}/{item_id}
//
// `fields` is an ordered list of `{ key, label, placeholder, wide?, type? }`
// that drives the form. `name` is always required and always the first field.
import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Pencil, X } from "lucide-react";

const blankFromFields = (fields) =>
  fields.reduce(
    (acc, f) => ({ ...acc, [f.key]: f.type === "checkbox" ? false : "" }),
    {},
  );

export default function ContactCardsSection({
  dogId,
  path,             // "groomers" | "trainers"
  singular,         // "groomer" | "trainer"
  plural,           // "groomers" | "trainers"
  Icon,
  overline,
  title,
  intro,
  fields,
  testidPrefix,
}) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);            // id being edited, or "new", or null
  const [form, setForm] = useState(blankFromFields(fields));
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!dogId) return;
    try {
      const { data } = await api.get(`/dogs/${dogId}/${path}`);
      setItems(data.items || []);
    } catch { setItems([]); }
  }, [dogId, path]);

  useEffect(() => { load(); }, [load]);

  const startAdd = () => {
    setForm(blankFromFields(fields));
    setEditing("new");
  };
  const startEdit = (it) => {
    setForm(fields.reduce((acc, f) => ({ ...acc, [f.key]: it[f.key] || "" }), {}));
    setEditing(it.id);
  };
  const cancel = () => { setEditing(null); setForm(blankFromFields(fields)); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) return;
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post(`/dogs/${dogId}/${path}`, form);
        toast.success(`${singular[0].toUpperCase() + singular.slice(1)} added`);
      } else {
        await api.put(`/dogs/${dogId}/${path}/${editing}`, form);
        toast.success("Saved");
      }
      await load();
      cancel();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/dogs/${dogId}/${path}/${id}`);
      await load();
    } catch { toast.error("Could not delete"); }
  };

  const isEditing = editing !== null;

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-section`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center">
            <Icon size={18} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">{overline}</p>
            <p className="font-display text-lg font-bold tracking-tight">{title}</p>
          </div>
        </div>
        {!isEditing ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={startAdd}
            data-testid={`${testidPrefix}-add`}
          >
            <Plus size={13} className="mr-1" /> Add {singular}
          </Button>
        ) : null}
      </div>
      {intro ? <p className="text-sm text-muted-foreground">{intro}</p> : null}

      {isEditing ? (
        <Card className="border-border shadow-none rounded-lg" data-testid={`${testidPrefix}-form`}>
          <CardContent className="p-5 sm:p-6">
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {fields.map((f) => (
                <div key={f.key} className={f.wide ? "sm:col-span-2" : ""}>
                  {f.type === "checkbox" ? (
                    <label className="flex items-center gap-2 mt-4 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={!!form[f.key]}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })}
                        className="h-4 w-4 accent-primary"
                        data-testid={`${testidPrefix}-field-${f.key}`}
                      />
                      <span className="text-sm">{f.label}</span>
                    </label>
                  ) : (
                    <>
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{f.label}</Label>
                      {f.type === "textarea" ? (
                        <Textarea
                          rows={2}
                          value={form[f.key] || ""}
                          onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                          placeholder={f.placeholder}
                          className="mt-1.5 text-sm"
                          data-testid={`${testidPrefix}-field-${f.key}`}
                        />
                      ) : (
                        <Input
                          type={f.type || "text"}
                          value={form[f.key] || ""}
                          onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                          placeholder={f.placeholder}
                          className="mt-1.5"
                          required={f.key === "name"}
                          maxLength={f.maxLength || 200}
                          data-testid={`${testidPrefix}-field-${f.key}`}
                        />
                      )}
                    </>
                  )}
                </div>
              ))}
              <div className="sm:col-span-2 flex items-center justify-end gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={cancel} data-testid={`${testidPrefix}-cancel`}>
                  <X size={13} className="mr-1" /> Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving || !form.name?.trim()}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid={`${testidPrefix}-save`}
                >
                  {saving ? "Saving…" : editing === "new" ? `Add ${singular}` : "Save"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : null}

      {items.length === 0 && !isEditing ? (
        <Card className="border-dashed border-border shadow-none rounded-lg">
          <CardContent className="p-6 text-sm text-muted-foreground text-center">
            No {plural} saved yet.
          </CardContent>
        </Card>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`${testidPrefix}-list`}>
          {items.map((it) => (
            <li key={it.id}>
              <Card className="border-border shadow-none rounded-lg" data-testid={`${testidPrefix}-item-${it.id}`}>
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="font-display font-bold text-base leading-tight truncate">{it.name}</p>
                        {fields.some((f) => f.type === "checkbox" && f.key === "emergency") && it.emergency ? (
                          <span
                            className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-destructive/10 text-destructive border border-destructive/30 font-semibold"
                            data-testid={`${testidPrefix}-emergency-${it.id}`}
                          >
                            Emergency
                          </span>
                        ) : null}
                      </div>
                      {fields.filter((f) => f.key !== "name" && f.type !== "checkbox").map((f) => {
                        const val = it[f.key];
                        if (!val) return null;
                        // Tap-to-call / tap-to-email so an ER contact is one
                        // press away. Sanitise phones to digits+`+`.
                        const isPhone = f.type === "tel" || f.key === "phone";
                        const isEmail = f.type === "email" || f.key === "email";
                        let renderedValue;
                        if (isPhone) {
                          const dialable = String(val).replace(/[^\d+]/g, "");
                          renderedValue = (
                            <a
                              href={`tel:${dialable}`}
                              className="text-primary hover:underline break-all"
                              data-testid={`${testidPrefix}-tel-${it.id}`}
                            >
                              {val}
                            </a>
                          );
                        } else if (isEmail) {
                          renderedValue = (
                            <a
                              href={`mailto:${val}`}
                              className="text-primary hover:underline break-all"
                              data-testid={`${testidPrefix}-mailto-${it.id}`}
                            >
                              {val}
                            </a>
                          );
                        } else if (f.type === "textarea") {
                          renderedValue = <span className="whitespace-pre-line">{val}</span>;
                        } else {
                          renderedValue = val;
                        }
                        return (
                          <p key={f.key} className="text-xs text-foreground/80">
                            <span className="uppercase tracking-wider text-[10px] text-muted-foreground">{f.label}: </span>
                            {renderedValue}
                          </p>
                        );
                      })}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => startEdit(it)}
                        className="text-muted-foreground hover:text-primary p-1"
                        title="Edit"
                        data-testid={`${testidPrefix}-edit-${it.id}`}
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(it.id)}
                        className="text-muted-foreground hover:text-destructive p-1"
                        title="Remove"
                        data-testid={`${testidPrefix}-remove-${it.id}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
